import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Plus, Edit, Trash2, GripVertical, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminThemes = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTheme, setEditingTheme] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchThemes();
  }, []);

  const fetchThemes = async () => {
    try {
      const response = await axios.get(`${API_URL}/themes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setThemes(response.data.sort((a, b) => a.index - b.index));
    } catch (error) {
      console.error('Erreur chargement thèmes:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (themeData) => {
    try {
      if (editingTheme?.id) {
        // Update
        await axios.put(
          `${API_URL}/admin/themes/${editingTheme.id}`,
          themeData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Thème mis à jour');
      } else {
        // Create
        await axios.post(
          `${API_URL}/admin/themes`,
          themeData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Thème créé');
      }
      fetchThemes();
      setIsDialogOpen(false);
      setEditingTheme(null);
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (themeId) => {
    if (!window.confirm('Supprimer ce thème ?')) return;

    try {
      await axios.delete(`${API_URL}/admin/themes/${themeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Thème supprimé');
      fetchThemes(); // Refetch pour refresh la liste
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/themes/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${response.data.count} thèmes réinitialisés`);
      setIsResetDialogOpen(false);
      fetchThemes(); // Refetch pour afficher les nouveaux thèmes
    } catch (error) {
      console.error('Erreur reset:', error);
      toast.error('Erreur lors de la réinitialisation');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-sage-700 font-sans">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 bg-sand-50">
      {/* Header */}
      <div className="bg-sage-500 px-6 py-8 text-white">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/admin/contents')}
          className="text-white hover:bg-sage-600 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-4xl font-serif font-light mb-2">Thèmes Audio</h1>
        <p className="text-white/80 font-sans">Gérer les 11 thèmes du programme</p>
      </div>

      {/* Add button */}
      <div className="px-6 py-6 space-y-3">
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => {
                setEditingTheme(null);
                setIsDialogOpen(true);
              }}
              className="w-full bg-sage-500 hover:bg-sage-600 text-white h-12 rounded-xl"
            >
              <Plus className="w-5 h-5 mr-2" />
              Ajouter un thème
            </Button>
          </DialogTrigger>
          <ThemeDialog
            theme={editingTheme}
            onSave={handleSave}
            onClose={() => setIsDialogOpen(false)}
          />
        </Dialog>

        {/* Reset button */}
        <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              className="w-full border-2 border-declic text-declic hover:bg-declic hover:text-white h-12 rounded-xl"
            >
              <RotateCcw className="w-5 h-5 mr-2" />
              Réinitialiser thèmes Déclic RePas
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-2xl font-serif text-sage-700">
                Réinitialiser les thèmes ?
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sage-700 font-sans mb-4">
                Cette action va <strong>supprimer tous les thèmes existants</strong> et les remplacer par les 11 thèmes officiels "Déclic RePas".
              </p>
              <p className="text-sage-700/70 font-sans text-sm">
                Thèmes qui seront créés : Choix, Envie émotionnelle, Estime, Liberté, Motivation, Paix, Plaisir, Pulsions, Rythme, Satiété, Tolérance
              </p>
            </div>
            <DialogFooter className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsResetDialogOpen(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleReset}
                disabled={resetting}
                className="flex-1 bg-declic hover:bg-declic/90 text-white"
              >
                {resetting ? 'Réinitialisation...' : 'Oui, je réinitialise'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Themes list */}
      <div className="px-6 space-y-3">
        {themes.map((theme) => (
          <div
            key={theme.id}
            className="bg-white rounded-xl border border-sand-200 p-5"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <GripVertical className="w-5 h-5 text-sage-700/40 mt-1" />
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-sm font-sans text-sage-700/60">Thème {theme.index}</span>
                    <span className="text-xs bg-sage-100 text-sage-700 px-2 py-0.5 rounded font-mono">
                      {theme.slug}
                    </span>
                  </div>
                  <h3 className="font-serif text-lg text-sage-700 mb-2">{theme.title}</h3>
                  {theme.description && (
                    <p className="text-sm text-sage-700/70 font-sans mb-3">{theme.description}</p>
                  )}
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="bg-sand-100 text-sage-700 px-2 py-1 rounded font-mono">
                      Zen: {theme.audio_zen_path}
                    </span>
                    <span className="bg-sand-100 text-sage-700 px-2 py-1 rounded font-mono">
                      Mouv: {theme.audio_movement_path}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex space-x-2 ml-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setEditingTheme(theme);
                    setIsDialogOpen(true);
                  }}
                  className="text-sage-500 hover:text-sage-700"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(theme.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ThemeDialog = ({ theme, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    index: theme?.index ?? 0,
    title: theme?.title ?? '',
    slug: theme?.slug ?? '',
    description: theme?.description ?? '',
    audio_zen_path: theme?.audio_zen_path ?? '/audios/-zen.mp3',
    audio_movement_path: theme?.audio_movement_path ?? '/audios/-movement.mp3',
    duration_zen: theme?.duration_zen ?? 0,
    duration_movement: theme?.duration_movement ?? 0
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-2xl font-serif text-sage-700">
          {theme ? 'Modifier le thème' : 'Nouveau thème'}
        </DialogTitle>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Ordre (Index)</Label>
            <Input
              type="number"
              value={formData.index}
              onChange={(e) => setFormData({ ...formData, index: parseInt(e.target.value) })}
              required
              min="0"
            />
          </div>
          <div>
            <Label>Slug (sans accents)</Label>
            <Input
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              placeholder="ex: respiration-ancrage"
              required
            />
          </div>
        </div>

        <div>
          <Label>Titre</Label>
          <Input
            value={formData.title}
            onChange={(e) => {
              setFormData({
                ...formData,
                title: e.target.value,
                slug: generateSlug(e.target.value)
              });
            }}
            placeholder="Ex: Respiration & Ancrage"
            required
          />
        </div>

        <div>
          <Label>Description</Label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Description courte du thème"
            rows={3}
          />
        </div>

        <div>
          <Label>Chemin Audio Zen</Label>
          <Input
            value={formData.audio_zen_path}
            onChange={(e) => setFormData({ ...formData, audio_zen_path: e.target.value })}
            placeholder="/audios/respiration-ancrage-zen.mp3"
            required
          />
        </div>

        <div>
          <Label>Chemin Audio Mouvement</Label>
          <Input
            value={formData.audio_movement_path}
            onChange={(e) => setFormData({ ...formData, audio_movement_path: e.target.value })}
            placeholder="/audios/respiration-ancrage-movement.mp3"
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Durée Zen (secondes)</Label>
            <Input
              type="number"
              value={formData.duration_zen}
              onChange={(e) => setFormData({ ...formData, duration_zen: parseInt(e.target.value) })}
              min="0"
            />
          </div>
          <div>
            <Label>Durée Mouvement (secondes)</Label>
            <Input
              type="number"
              value={formData.duration_movement}
              onChange={(e) => setFormData({ ...formData, duration_movement: parseInt(e.target.value) })}
              min="0"
            />
          </div>
        </div>

        <div className="flex space-x-3">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            Annuler
          </Button>
          <Button type="submit" className="flex-1 bg-sage-500 hover:bg-sage-600 text-white">
            Enregistrer
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

export default AdminThemes;
